import React, {
  Dispatch,
  FC,
  forwardRef,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';

import { PlayerInfo } from '../../../types';
import { useLeaveRoom } from '../../../components';
import {
  BhabiLink,
  ErrorLayout,
  UserAvatarStateBadge,
} from '../../../../../shared/components';
import {
  Avatar,
  Box,
  Button,
  Chip,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';
import { PlayersAvatarWrapper } from './styled';
import { PUBLIC_PROFILE_ROUTE } from '../../../profile';
import { useLandscapeMode } from '@/shared/hooks';
import { useTableOptions } from '../../table-options';

export interface PlayerPosition {
  id: string;
  isHorizontal: boolean;
  top: number;
  right: number;
  left: number;
  bottom: number;
}

export interface InRoomPlayerListProps {
  noOfPlayers: number | null;
  players: PlayerInfo[];
  nextTurn: string;
  setPlayerPosition?: Dispatch<SetStateAction<PlayerPosition[]>>;
  isHorizontal?: boolean;
}

export const InRoomPlayerList: FC<InRoomPlayerListProps> = ({
  noOfPlayers,
  players,
  nextTurn,
  setPlayerPosition,
  isHorizontal = false,
}) => {
  const leaveRoom = useLeaveRoom();
  const isLandscape = useLandscapeMode();
  const { isGuestTable } = useTableOptions();

  if (!noOfPlayers) {
    return (
      <ErrorLayout
        outerBoxProps={{
          height: `auto`,
        }}
      >
        <Button
          variant="contained"
          onClick={leaveRoom(
            `Are you sure you want to leave this room?`,
            false
          )}
        >
          Leave Room
        </Button>
      </ErrorLayout>
    );
  }

  if (!isHorizontal && players.length === 0 && !isLandscape) {
    return null;
  }

  return (
    <PlayersAvatarWrapper isHorizontal={isHorizontal}>
      {players.map((player, i) => {
        return (
          <Box
            key={i}
            display="flex"
            flexDirection="column"
            gap={{
              sm: 0.5,
              lg: 1.5,
            }}
            alignItems="center"
          >
            <PlayerAvatar
              isHorizontal={isHorizontal}
              setPlayerPosition={setPlayerPosition}
              player={player}
              nextTurn={nextTurn}
              i={i}
            />
            <Chip
              label={
                isGuestTable
                  ? `${player.name} · ${player.cardsLeft}`
                  : player.name
              }
              size="small"
              sx={theme => ({
                maxWidth: 90,
                position: 'relative',
                height: {
                  sm: 16,
                  lg: 'inherit',
                },
                fontSize: { lg: '0.9rem', sm: '0.65rem' },
                fontWeight: 500,
              })}
            />
          </Box>
        );
      })}
    </PlayersAvatarWrapper>
  );
};

export interface PlayerAvatarProps {
  player: PlayerInfo;
  nextTurn: string;
  i: number;
  playerAvatarSx?: SxProps<Theme>;
  setPlayerPosition?: Dispatch<SetStateAction<PlayerPosition[]>>;
  isHorizontal?: boolean;
}

export const PlayerAvatar: FC<PlayerAvatarProps> = forwardRef<
  HTMLDivElement,
  PlayerAvatarProps
>(
  (
    { player, nextTurn, i, playerAvatarSx, setPlayerPosition, isHorizontal },
    ref
  ) => {
    const theme = useTheme();
    const { sx, src } = avatarSxAndSrc(
      player.id,
      nextTurn || '',
      player.cardsLeft,
      player.photoURL
    )(theme);

    const playerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (playerRef.current && setPlayerPosition) {
        const pos = playerRef.current.getBoundingClientRect();
        setPlayerPosition(prev => {
          if (prev.some(p => p.id === player.id)) {
            return prev;
          }
          return [
            ...prev,
            {
              id: player.id,
              isHorizontal: !!isHorizontal,
              top: pos.top,
              right: pos.right,
              left: pos.left,
              bottom: pos.bottom,
            },
          ];
        });
      }
    }, [isHorizontal, setPlayerPosition, player.id]);

    const { isGuestTable } = useTableOptions();
    const isTurn = nextTurn === player.id;
    const avatar = (
      <UserAvatarStateBadge state={player.state}>
        <Avatar
          sx={{
            position: 'relative',
            ...sx,
            ...playerAvatarSx,
          }}
          sizes="large"
          className="player-avatar"
          ref={ref}
          src={src}
        >
          {playerNameOrPhotoURL(
            player.id,
            player.cardsLeft,
            nextTurn,
            player.photoURL,
            player.name
          )}
        </Avatar>
      </UserAvatarStateBadge>
    );
    const label = `${player.name}${isTurn ? ', playing now' : ''}, ${player.cardsLeft} card${player.cardsLeft === 1 ? '' : 's'} left`;

    return (
      <div
        ref={playerRef}
        className="st-seat"
        data-turn={isTurn ? 'true' : undefined}
        data-escaped={player.cardsLeft === 0 ? 'true' : undefined}
      >
        {isGuestTable ? (
          <span role="img" aria-label={label}>
            {avatar}
          </span>
        ) : (
          <BhabiLink
            aria-label={label}
            to={[PUBLIC_PROFILE_ROUTE.EXACT_PATH, '?user_id=', player.id].join(
              ''
            )}
          >
            {avatar}
          </BhabiLink>
        )}
      </div>
    );
  }
);

PlayerAvatar.displayName = 'PlayerAvatar';

function playerNameOrPhotoURL(
  playerID: string,
  cardsLeft: number,
  nextTurn?: string | null,
  photoURL?: string,
  name?: string
) {
  if (nextTurn === playerID) {
    return cardsLeft;
  }

  if (!photoURL && name?.length) {
    return name[0].toUpperCase();
  }

  return null;
}

function avatarSxAndSrc(
  playerID: string,
  nextTurn: string,
  cardsLeft: number,
  photoURL: string
) {
  return (theme: Theme) => {
    const isCurrentPlayerWithNextTurn = nextTurn === playerID;
    const isNoCardsLeft = cardsLeft === 0;
    const width = {
      lg: 56,
      sm: 40,
    };

    const src = photoURL?.length
      ? {
          src: isCurrentPlayerWithNextTurn ? '' : photoURL,
        }
      : {};

    return {
      sx: {
        width,
        height: width,
        color: theme.palette.primary.main,
        fontSize: '0.9rem',
        fontWeight: 700,
        marginBottom: theme.spacing(0.2),
        ...(isCurrentPlayerWithNextTurn && {
          animation: 'pulse-border 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
          '@keyframes pulse-border': {
            '0%, 100%': {
              boxShadow: `0 0 0 4px ${theme.palette.warning.main}, 0 0 0 8px ${theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.4)' : 'rgba(255, 152, 0, 0.5)'}`,
            },
            '50%': {
              boxShadow: `0 0 0 4px ${theme.palette.warning.main}, 0 0 0 12px ${theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255, 152, 0, 0.2)'}`,
            },
          },
        }),
        ...(isNoCardsLeft && {
          '&::after': {
            content: '"✓"',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            fontWeight: 'bold',
            opacity: 0.85,
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            '@keyframes pulse': {
              '0%, 100%': {
                opacity: 0.9,
              },
              '50%': {
                opacity: 0.6,
              },
            },
          },
        }),
      },
      src: src.src,
    };
  };
}
